// "Variantes de acabado" — `POST /v1/generate/variantes-acabado` del atlas-backend
// (contrato: `atlas-backend/docs/2026-09-23-variantes-de-acabado-contrato-front.md`,
// ADR-2026-09-23-variantes-de-acabado-se-conecta-al-backend).
//
// Una tanda es foto de partida × acabado: el backend genera UNA imagen por par —el mismo
// mueble con ese acabado cambiado y nada más— y devuelve un resultado por par. Responde
// 202 con el `execution_id`; la tanda se sigue por `GET /v1/jobs/:id` con
// `pollJobUntilSettled`, como los demás flujos que no esperan en el request.
//
// Lo que viaja: referencias de STORAGE (bucket + path) para las fotos y las muestras
// subidas, nunca URLs firmadas ni binarios; el `id` de fila para las creaciones. La
// traducción del estado de la pantalla al body vive en `armarPedidoVariantes`, que es
// pura y se testea en `variantesAcabado.test.ts`.

import { atlasBackendFetch, pollJobUntilSettled } from './atlasBackend';
import { GenerateAdError } from './generateAdRequest';
import type { ResolucionImagen } from './resolucionImagen';

/** Los ratios que el backend acepta: los mismos siete de `FORMAT_OPTIONS_V2`. */
export type FormatoVariantes = '1:1' | '4:5' | '2:3' | '9:16' | '3:2' | '16:9' | '21:9';

/** Los ejes del panel, tal cual los ids del dominio (`variantesAcabado.ts` de la pantalla). */
export type EjeAcabado = 'color' | 'tapizado' | 'madera' | 'superficie' | 'mis-muestras';

export type FondoVariantes = 'original' | 'estudio' | 'ambiente' | 'describir';

/** Referencia de storage de algo que la pantalla subió (foto de partida o muestra). */
export interface ReferenciaSubida {
  bucket: string;
  path: string;
}

export interface AcabadoPedido {
  id: string;
  nombre: string;
  categoria: EjeAcabado;
  /** Sólo lo lee el eje `color`; en los otros el backend lo ignora a propósito. */
  color?: string;
  swatch_bucket?: string;
  swatch_path?: string;
}

export interface ImagenPedida {
  image_id?: string;
  image_bucket?: string;
  image_path?: string;
  acabados: AcabadoPedido[];
  fondo: FondoVariantes;
  fondo_descripcion?: string;
}

export interface PedidoVariantes {
  imagenes: ImagenPedida[];
  formato: FormatoVariantes;
  image_size: ResolucionImagen;
  instruccion?: string;
  modo_utilizado: 'variantes_acabado';
}

/** Lo que la pantalla sabe de cada foto de partida al momento de generar. */
export interface FotoParaPedido {
  /** `generada`: una creación (viaja su `id`). `subida`: un archivo, ya en storage. */
  origen: { tipo: 'generada'; id: string } | { tipo: 'subida'; ref: ReferenciaSubida };
  acabados: Array<{
    id: string;
    nombre: string;
    categoria: EjeAcabado;
    color: string;
    /** La muestra subida, ya en storage. Sólo `mis-muestras`. */
    swatch?: ReferenciaSubida;
  }>;
  fondo: FondoVariantes;
  /** El texto del fondo `describir`; con otro fondo se ignora. */
  fondoDescripcion?: string | null;
}

/**
 * El body del endpoint a partir de lo elegido. Pura.
 *
 * Reglas del contrato que se aplican acá y no en la pantalla: `fondo_descripcion` viaja
 * sólo con `describir`; el hex viaja sólo con el eje `color` (en los otros es neutro a
 * propósito y el backend lo ignoraría igual); la instrucción vacía no viaja.
 */
export function armarPedidoVariantes(p: {
  fotos: FotoParaPedido[];
  formato: FormatoVariantes;
  calidad: ResolucionImagen;
  instruccion?: string | null;
}): PedidoVariantes {
  const instruccion = (p.instruccion ?? '').trim();
  return {
    imagenes: p.fotos.map((f) => ({
      ...(f.origen.tipo === 'generada'
        ? { image_id: f.origen.id }
        : { image_bucket: f.origen.ref.bucket, image_path: f.origen.ref.path }),
      acabados: f.acabados.map((a) => ({
        id: a.id,
        nombre: a.nombre,
        categoria: a.categoria,
        ...(a.categoria === 'color' ? { color: a.color } : {}),
        ...(a.swatch ? { swatch_bucket: a.swatch.bucket, swatch_path: a.swatch.path } : {}),
      })),
      fondo: f.fondo,
      ...(f.fondo === 'describir' && (f.fondoDescripcion ?? '').trim()
        ? { fondo_descripcion: (f.fondoDescripcion ?? '').trim() }
        : {}),
    })),
    formato: p.formato,
    image_size: p.calidad,
    ...(instruccion ? { instruccion } : {}),
    modo_utilizado: 'variantes_acabado',
  };
}

export interface TandaLanzada {
  executionId: string;
  imagenes: number;
  creditosEstimados: number;
}

// Mismo copy de saldo que las generaciones de imagen (generateAdRequest / generateAngles).
const BILLING_MESSAGE =
  'No te alcanzan los créditos para esta generación. Sumá créditos o actualizá tu plan para continuar.';

/**
 * Dispara la tanda. 202 ⇒ `TandaLanzada`; 402 ⇒ `GenerateAdError('billing')` (accionable:
 * el modal de plan); 400 ⇒ `GenerateAdError('technical')` con el mensaje del backend, que
 * ya viene en castellano y dice qué campo ("la tanda tiene 25 imágenes; el tope son 24").
 */
export async function postVariantesAcabado(body: PedidoVariantes): Promise<TandaLanzada> {
  const res = await atlasBackendFetch('/v1/generate/variantes-acabado', body as unknown as Record<string, unknown>);
  if (res.status === 402) {
    throw new GenerateAdError('billing', BILLING_MESSAGE, 'billing');
  }
  const data = (await res.json().catch(() => null)) as {
    execution_id?: string;
    imagenes?: number;
    creditos_estimados?: number;
    message?: string;
  } | null;
  if (!res.ok || !data?.execution_id) {
    throw new GenerateAdError(
      'technical',
      data?.message || 'No se pudo generar la tanda. Intentá nuevamente.',
      data?.message ?? null,
    );
  }
  return {
    executionId: data.execution_id,
    imagenes: data.imagenes ?? 0,
    creditosEstimados: data.creditos_estimados ?? 0,
  };
}

/** Un par de la tanda, como lo devuelve el job: o la imagen, o por qué no. */
export interface ResultadoPar {
  imagen_index: number;
  acabado_id: string;
  imagen_generada_id?: string;
  error?: { type: string; message: string };
}

export interface TandaTerminada {
  /** `null` = el job entero falló (ninguna imagen salió) o se agotó la espera. */
  resultados: ResultadoPar[] | null;
  /** Mensaje mostrable cuando falló entero (viene del backend). */
  mensaje: string | null;
  /**
   * Se agotó la espera de ACÁ, no la del backend: el job sigue vivo y sus imágenes van a
   * caer en Creaciones —y se van a cobrar— aunque esta pantalla ya no las esté mirando.
   *
   * Existe porque `resultados: null` decía dos cosas distintas y el modo las trataba
   * igual: marcaba todas las celdas "no salió · no se cobró", que es falso justo en este
   * caso (ADR-2026-09-25-revision-greptile-la-tanda-aceptada-se-puede-reanudar).
   */
  sigueEnCurso: boolean;
}

/**
 * Espera el job de la tanda. El backend cancela (sin cobrar) lo que no llega a su propio
 * deadline —180 s + 20 s por par, techo 15 min—, así que acá se espera lo mismo y un poco
 * más: si esto vence primero, el job puede seguir y las imágenes caen igual en Creaciones.
 */
export async function esperarVariantes(
  executionId: string,
  pares: number,
  opts?: { signal?: AbortSignal },
): Promise<TandaTerminada> {
  const timeoutMs = Math.min(16 * 60 * 1000, 200_000 + 20_000 * pares);
  const job = await pollJobUntilSettled(executionId, {
    timeoutMs,
    intervalMs: 3000,
    signal: opts?.signal,
  });
  if (!job) {
    return {
      resultados: null,
      mensaje: 'La tanda está tardando más de lo normal. Las imágenes van a aparecer en Creaciones cuando terminen.',
      sigueEnCurso: true,
    };
  }
  if (job.status === 'failed') {
    return {
      resultados: null,
      mensaje: job.userMessage ?? 'No se pudo generar la tanda. Intentá nuevamente.',
      sigueEnCurso: false,
    };
  }
  const resultados = (job.output as { resultados?: ResultadoPar[] } | null)?.resultados;
  return {
    resultados: Array.isArray(resultados) ? resultados : [],
    mensaje: null,
    sigueEnCurso: false,
  };
}

/** Texto corto para la celda de un par que falló, a partir de la taxonomía del backend. */
export function textoDeErrorDePar(error: ResultadoPar['error'] | undefined): string {
  switch (error?.type) {
    case 'policy_violation':
    case 'model_refusal':
      return 'El modelo no pudo con esta foto';
    case 'provider_timeout':
      return 'Se quedó sin tiempo · no se cobró';
    case 'billing':
      return 'Sin créditos';
    case 'validation':
      return 'La foto no se pudo usar';
    default:
      return 'No salió · no se cobró';
  }
}
